import { styled, Typography } from "@mui/material";

import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { CellContext, ColumnDef } from "@tanstack/react-table";

export interface SampleRow {
  id: number;
  rowIndex: number;
  rowNumber: number;
  query: string;
  formattedAddress: string;
  matchQuality: string;
  matchScore: string;
  matchType: string;
  matchDistance: number;
  matchMethod: string;
  longitude: number;
  latitude: number;
}

// No offset, No Max Width
export const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    slotProps={{
      popper: {
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, -14],
            },
          },
        ],
      },
    }}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
});

// Helper to create a cell renderer with tooltip for a given field
const cellWithTooltip = (fieldName: string) => ({
  cell: ({ row }: CellContext<SampleRow, unknown>) => (
    <CustomTooltip title={row.getValue(fieldName)}>
      <Typography variant="body2" noWrap>
        {row.getValue(fieldName)}
      </Typography>
    </CustomTooltip>
  ),
});

export const extractRowData = (sampleResponse: Record<string, unknown>): SampleRow[] => {
  const response = sampleResponse as {
    rowIndexes: number[];
    queries: string[];
    featureCollection: { features: Array<{ properties: { formattedAddress: string }; matchQuality: string; matchScore: string; matchType: string; matchDistance: number; matchMethod: string; geometry: { coordinates: [number, number] } } | null> };
  };
  var rows: SampleRow[] = [];

  // Loop through each of the row indexes
  for (var i = 0; i < response.rowIndexes.length; i++) {
    const feature = response.featureCollection.features[i];

    // Check if the row has a result
    const hasResult = feature !== undefined && feature !== null;

    rows.push({
      id: response.rowIndexes[i],
      rowIndex: response.rowIndexes[i],
      rowNumber: response.rowIndexes[i] + 1,
      query: response.queries[i],
      formattedAddress: hasResult
        ? feature.properties.formattedAddress
        : "NO ADDRESS MATCHED THE QUERY",
      matchQuality: hasResult ? feature.matchQuality : "",
      matchScore: hasResult ? feature.matchScore : "",
      matchType: hasResult ? feature.matchType : "",
      matchDistance: hasResult ? feature.matchDistance : 0,
      matchMethod: hasResult ? feature.matchMethod : "",
      longitude: hasResult ? feature.geometry.coordinates[0] : 0,
      latitude: hasResult ? feature.geometry.coordinates[1] : 0,
    });
  }

  return rows;
};

export const defineColumns = (jobType: string) => {
  const columns: ColumnDef<SampleRow>[] = [
    { size: 100, accessorKey: "rowNumber", header: "Row Number" },
    {
      accessorKey: "query",
      header: "Query",
      ...cellWithTooltip("query"),
    },
    {
      accessorKey: "formattedAddress",
      header: "Formatted Address",
      ...cellWithTooltip("formattedAddress"),
    },
  ];

  if (jobType == "addressGeocoder") {
    columns.push(
      { size: 100, accessorKey: "matchQuality", header: "Match Quality" },
      { size: 100, accessorKey: "matchScore", header: "Match Score" }
    );
  } else if (jobType == "reverseGeocoder") {
    columns.push(
      { size: 200, accessorKey: "matchType", header: "Match Type" },
      { size: 130, accessorKey: "matchDistance", header: "Match Distance" },
      { size: 200, accessorKey: "matchMethod", header: "Match Method" }
    );
  } else {
    console.error("Unknown batch job type");
  }

  return columns;
};
